<script setup lang="ts">
import { ref, onMounted } from 'vue'

import PrevStepBtn from '@/components/frontend/profile/common/PrevStepBtn.vue'

import { notifySuccess } from '@/utils/ui/handleNotification'

import * as aggr from '@/api-generator/aggr'
import { feUserProfileAPI } from '@/APIs'
import { ProfileSteps } from '@/constant/user'
import { MOBILE_NUMBER_PATTERN } from '@/constant/validation'
import appStore from '@/stores'

import type { FormInstance, FormRules } from 'element-plus'

interface PhoneFormModel {
  newPhoneNumber: string
}

const feUserProfileStore = appStore.useFEUserProfileStore

const updatePhoneFormRef = ref<null | FormInstance>(null)

const updatePhoneForm = ref<PhoneFormModel>({
  newPhoneNumber: ''
})

const validatePhone = (rule: any, value: string, callback: any) => {
  if (value === '') {
    callback(new Error('請輸入手機號碼'))
  } else {
    if (!MOBILE_NUMBER_PATTERN.test(value)) {
      callback(new Error('請輸入正確的手機號碼'))
    } else {
      callback()
    }
  }
}

const rules = ref<FormRules<PhoneFormModel>>({
  newPhoneNumber: [{ validator: validatePhone, trigger: ['blur', 'change'] }]
})

const prevStep = ref<ProfileSteps>(ProfileSteps.PROFILE_INFO)
const isLoading = ref(false)

const handleUpdatePhone = async () => {
  const reqBody: aggr.FEUserProfileUpdatePhoneNumberRq = {
    newPhoneNumber: updatePhoneForm.value.newPhoneNumber
  }
  isLoading.value = true
  try {
    await feUserProfileAPI.updateUserPhoneNumber(reqBody)
    feUserProfileStore.setUserPhoneNumber(updatePhoneForm.value.newPhoneNumber)
    notifySuccess('手機變更成功')
    feUserProfileStore.updateCurrentStep(ProfileSteps.PROFILE_INFO)
  } catch (error: any) {
    const errorMsg = error.response.data.message ? error.response.data.message : error.message
    console.error(errorMsg)
  } finally {
    isLoading.value = false
  }
}

const submitForm = (formEl: FormInstance | null) => {
  if (!formEl) {
    return
  }
  formEl.validate((valid) => {
    if (valid) {
      handleUpdatePhone()
    } else {
      console.error('表單驗證失敗')
    }
  })
}

const newPhoneInputRef = ref<null | HTMLInputElement>(null)
onMounted(() => {
  newPhoneInputRef.value?.focus()
})
</script>

<template>
  <div class="profile-edit-phone">
    <PrevStepBtn :prev-step="prevStep" />
    <div>
      <h3 class="profile-step-title">變更手機門號</h3>
      <el-form
        ref="updatePhoneFormRef"
        :rules="rules"
        :model="updatePhoneForm"
        @keypress.enter.prevent="submitForm(updatePhoneFormRef)"
      >
        <el-form-item class="profile-edit-phone-item" prop="newPhoneNumber">
          <label class="profile-label" for="currentPhone">請輸入您欲變更的新手機門號</label>
          <el-input
            id="currentPhone"
            ref="newPhoneInputRef"
            v-model="updatePhoneForm.newPhoneNumber"
            type="text"
            maxlength="10"
            show-word-limit
          />
        </el-form-item>
        <div class="profile-steps-btn-wrapper">
          <BaseButton class="w-[120px]" @click="feUserProfileStore.updateCurrentStep(ProfileSteps.PROFILE_INFO)"
            >取消
          </BaseButton>
          <BaseButton
            class="w-[120px] border-app-brand-blue-500 bg-app-brand-blue-500 text-white"
            :disabled="isLoading"
            @click="submitForm(updatePhoneFormRef)"
          >
            <span v-if="isLoading">儲存中</span>
            <span v-else>儲存變更</span>
          </BaseButton>
        </div>
      </el-form>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.profile-edit-phone {
  &-item {
    position: relative;
    margin-bottom: 12px;
  }
}
.error-msg-wrapper {
  position: absolute;
  bottom: -24px;
  left: 0;
}
</style>
